import React, { useEffect, useState } from "react";
import { parseISO, isValid, format as fmt } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import axios from "axios";
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Input, 
  Select, 
  DatePicker, 
  Table, 
  Tag, 
  Space, 
  Statistic,
  Badge,
  Checkbox,
  Tooltip,
  Spin,
  AutoComplete
} from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FilterOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  TagOutlined,
  ReloadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import moment from 'moment';

import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import fetchData from "../../functions/fetchData";
import { fetchComboData } from "../../services/api";
import api from "../../api/api";
import { getHeaders } from "../../utility/auth";
import logo from "../../assets/logo.png";

const { RangePicker } = DatePicker;
const { Option } = Select;

export default function SaleLedger() {
  const [data, setData] = useState([]);
  const [Alldata, setAllData] = useState([]);
  const [customerdata, setCustomerdata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [Totals, setTotals] = useState({ credit: 0, debit: 0, balance: 0 });
  const [summaryLabel, setSummaryLabel] = useState('Pending');
  const [quickFilter, setQuickFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectedTotals, setSelectedTotals] = useState({ debit: 0, credit: 0, balance: 0 });
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [highlightedCustomerIndex, setHighlightedCustomerIndex] = useState(-1);
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState("");
  const [companyProfile, setCompanyProfile] = useState(null);
  
  const [formdata, setFormData] = useState({
    from: "",
    to: "",
    accounttype: "",
    customername: "",
    account_id: "",
    status: "all",
    recordStatus: "all",
    category: "pending_bill",
    showOnlyFinalBalance: false
  });

  const quickFilters = [
    { key: 'all', label: 'All Records', icon: EyeOutlined, color: 'default' },
    { key: 'pending', label: 'Pending', icon: ClockCircleOutlined, color: 'warning' },
    { key: 'completed', label: 'Completed', icon: CheckCircleOutlined, color: 'success' },
    { key: 'overdue', label: 'Overdue', icon: ExclamationCircleOutlined, color: 'error' }
  ];

  const accountTypes = [
    { value: '', label: 'All Account Types' },
    { value: 'Sales', label: '💰 Sales' },
    { value: 'Cash', label: '💵 Cash' },
    { value: 'Discount', label: '🏷️ Discount' },
    { value: 'Account Recievable', label: '📋 Account Receivable' },
    { value: 'Purchase', label: '🛒 Purchase' },
    { value: 'Expense', label: '💸 Expense' }
  ];

  const categoryOptions = [
    { value: 'pending_bill', label: 'Pending Bill' },
    { value: 'sponsor', label: 'Sponsor' },
    { value: 'advance', label: 'Advance' },
    { value: 'other', label: 'Other' }
  ];

  const formatDateSafe = (dateStr) => {
    const date = parseISO(dateStr);
    return isValid(date) ? fmt(date, "yyyy-MM-dd") : "";
  };

  const formatDisplayDate = (dateStr) => {
    const date = parseISO(dateStr);
    return isValid(date) ? fmt(date, "dd/MM/yyyy") : "";
  };

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const resetFilters = () => {
    setFormData({
      from: "",
      to: "",
      accounttype: "",
      customername: "",
      account_id: "",
      status: "all",
      recordStatus: "all",
      category: "pending_bill",
      showOnlyFinalBalance: false
    });
    setQuickFilter('all');
    setSearchTerm('');
    setCustomerSearchTerm('');
    setShowCustomerDropdown(false);
    setHighlightedCustomerIndex(-1);
    setSelectedCustomerFilter(''); // Clear customer filter
    setCurrentPage(1);
    setSelectedRecords([]);
    setData(Alldata); // Reset to all data immediately
    // Force re-render by clearing filters
    setTimeout(() => {
      setData(Alldata);
    }, 0);
  };

  // Handle checkbox selection
  const handleCheckboxChange = (record) => {
    const isSelected = selectedRecords.some(r => r.id === record.id);
    let newSelectedRecords;
    
    if (isSelected) {
      newSelectedRecords = selectedRecords.filter(r => r.id !== record.id);
    } else {
      newSelectedRecords = [...selectedRecords, record];
    }
    
    setSelectedRecords(newSelectedRecords);
    
    // Calculate totals for selected records
    const debit = newSelectedRecords.reduce((sum, r) => sum + (Number(r.debit_amount) || 0), 0);
    const credit = newSelectedRecords.reduce((sum, r) => sum + (Number(r.credit_amount) || 0), 0);
    setSelectedTotals({ debit, credit, balance: debit - credit });
  };

  // Handle select all checkbox
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRecords([...currentRecords]);
      const debit = currentRecords.reduce((sum, r) => sum + (Number(r.debit_amount) || 0), 0);
      const credit = currentRecords.reduce((sum, r) => sum + (Number(r.credit_amount) || 0), 0);
      setSelectedTotals({ debit, credit, balance: debit - credit });
    } else {
      setSelectedRecords([]);
      setSelectedTotals({ debit: 0, credit: 0, balance: 0 });
    }
  };

  // Mark selected records as completed
  const handleMarkAsCompleted = async () => {
    if (selectedRecords.length === 0) {
      toast.warning('Please select at least one record');
      return;
    }

    try {
      // Get current user ID from localStorage or sessionStorage
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      const userId = userStr ? JSON.parse(userStr).id : (localStorage.getItem('uname') || sessionStorage.getItem('uname'));
      
      if (!userId) {
        toast.error('User ID not found. Please log in again.');
        return;
      }

      // Update each selected record and create history entry
      const updatePromises = selectedRecords.map(async (record) => {
        // Update the ledger entry status
        await axios.put(
          `/updatedata/customer_ledger_entries`,
          {
            updatedFields: {
              status: 'completed',
              updated_by_id: userId
            },
            where: {
              id: record.id
            }
          },
          getHeaders()
        );

        // Create history record
        await axios.post(
          `/insertdata/ledger_status_history`,
          {
            ledger_type: 'customer',
            ledger_entry_id: record.id,
            previous_status: record.status || 'pending',
            new_status: 'completed',
            changed_by_id: userId
          },
          getHeaders()
        );
      });

      await Promise.all(updatePromises);

      // Update local state in place instead of refetching. A raw refetch here would
      // drop the receipt_type category filter and the customer_name enrichment that
      // only the initial load applies, so the table would look wrong until a hard
      // refresh re-ran that load - update the matching records instead.
      const completedIds = new Set(selectedRecords.map(r => r.id));
      setAllData(prev => prev.map(r => completedIds.has(r.id) ? { ...r, status: 'completed' } : r));

      // Clear selections
      setSelectedRecords([]);
      setSelectedTotals({ debit: 0, credit: 0, balance: 0 });

      toast.success(`Successfully marked ${updatePromises.length} record(s) as completed`);
    } catch (error) {
      console.error('Error updating records:', error);
      toast.error('Failed to update records');
    }
  };

  // Revert selected completed records back to pending
  const handleRevertCompleted = async () => {
    if (selectedRecords.length === 0) {
      toast.warning('Please select at least one record');
      return;
    }

    // Check if all selected records are completed
    const allCompleted = selectedRecords.every(r => r.status === 'completed');
    if (!allCompleted) {
      toast.warning('Can only revert records with completed status');
      return;
    }

    try {
      // Get current user ID from localStorage or sessionStorage
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      const userId = userStr ? JSON.parse(userStr).id : (localStorage.getItem('uname') || sessionStorage.getItem('uname'));
      
      if (!userId) {
        toast.error('User ID not found. Please log in again.');
        return;
      }

      // Revert each selected record
      const revertPromises = selectedRecords.map(async (record) => {
        // Update the ledger entry status back to pending
        await axios.put(
          `/updatedata/customer_ledger_entries`,
          {
            updatedFields: {
              status: 'pending',
              updated_by_id: userId
            },
            where: {
              id: record.id
            }
          },
          getHeaders()
        );

        // Mark history records as reverted
        await axios.put(
          `/updatedata/ledger_status_history`,
          {
            updatedFields: {
              reverted: 1,
              reverted_at: new Date().toISOString(),
              reverted_by_id: userId
            },
            where: {
              ledger_type: 'customer',
              ledger_entry_id: record.id,
              new_status: 'completed',
              reverted: 0
            }
          },
          getHeaders()
        );

        // Create new history record for the revert action
        await axios.post(
          `/insertdata/ledger_status_history`,
          {
            ledger_type: 'customer',
            ledger_entry_id: record.id,
            previous_status: 'completed',
            new_status: 'pending',
            changed_by_id: userId
          },
          getHeaders()
        );
      });

      await Promise.all(revertPromises);

      // Update local state in place instead of refetching (see handleMarkAsCompleted
      // for why a raw refetch here would lose the category filter / customer_name).
      const revertedIds = new Set(selectedRecords.map(r => r.id));
      setAllData(prev => prev.map(r => revertedIds.has(r.id) ? { ...r, status: 'pending' } : r));

      // Clear selections
      setSelectedRecords([]);
      setSelectedTotals({ debit: 0, credit: 0, balance: 0 });

      toast.success(`Successfully reverted ${revertPromises.length} record(s) to pending`);
    } catch (error) {
      console.error('Error reverting records:', error);
      toast.error('Failed to revert records');
    }
  };

  const getVisibleRecordsForExport = () => {
    return data;
  };

  const getVisibleTotalsForExport = (records) => records.reduce(
    (acc, row) => {
      const debit = Number(row.debit_amount) || 0;
      const credit = Number(row.credit_amount) || 0;
      acc.debit += debit;
      acc.credit += credit;
      acc.balance += debit - credit;
      return acc;
    },
    { debit: 0, credit: 0, balance: 0 }
  );

  const getActiveCustomerName = () => {
    if (selectedCustomerFilter) {
      return selectedCustomerFilter;
    }

    if (formdata.customername) {
      return formdata.customername;
    }

    if (formdata.account_id) {
      const matchedCustomer = customerdata.find(
        (customer) => customer.id === parseInt(formdata.account_id)
      );
      return matchedCustomer?.name || "";
    }

    return "";
  };

  const exportToPDF = async () => {
    const visibleRecords = getVisibleRecordsForExport();
    const visibleTotals = getVisibleTotalsForExport(visibleRecords);
    const activeCustomerName = getActiveCustomerName();

    if (!visibleRecords.length) {
      toast.warning("No visible records to export");
      return;
    }

    const doc = new jsPDF('landscape'); // Use landscape for better table width
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let currentY = 15;
    
    // Header Section with Soft Light Blue Background
    doc.setFillColor(230, 240, 255); // Soft light blue
    doc.rect(0, 0, pageWidth, 25, 'F');
    
    // Add border
    doc.setDrawColor(180, 200, 230); // Soft blue border
    doc.setLineWidth(0.5);
    doc.rect(0, 0, pageWidth, 25, 'S');
    
    // Add logo
    const imgWidth = 35;
    const imgHeight = 15;
    doc.addImage(logo, 'PNG', 14, 5, imgWidth, imgHeight);
    
    // Company/Report Title
    doc.setTextColor(40, 80, 150); // Soft dark blue
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('CUSTOMER LEDGER REPORT', pageWidth / 2, currentY, { align: 'center' });
    
    // Generated timestamp
    doc.setFontSize(7);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(80, 80, 80); // Soft gray
    doc.text(`Generated: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`, pageWidth - 14, currentY, { align: 'right' });
    currentY = 30;
    
    // Reset text color for body
    doc.setTextColor(60, 60, 60); // Soft dark gray
    
    // Report Filters Section
    if (activeCustomerName || formdata.from || formdata.to || formdata.accounttype || formdata.showOnlyFinalBalance || formdata.recordStatus !== 'all' || formdata.category) {
      doc.setFillColor(250, 252, 255); // Very soft blue
      doc.rect(5, currentY, pageWidth - 10, 14, 'F');
      doc.setDrawColor(200, 210, 230); // Soft blue border
      doc.rect(5, currentY, pageWidth - 10, 14, 'S');
      
      currentY += 5;
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('Report Filters:', 8, currentY);
      
      let filterText = [];
      if (activeCustomerName) filterText.push(`Customer: ${activeCustomerName}`);
      if (formdata.accounttype) filterText.push(`Account Type: ${formdata.accounttype}`);
      if (formdata.from && formdata.to) {
        filterText.push(`Period: ${formatDisplayDate(formdata.from)} to ${formatDisplayDate(formdata.to)}`);
      }
      if (formdata.recordStatus !== 'all') filterText.push(`Record Status: ${formdata.recordStatus}`);
      if (formdata.category) {
        const selectedCategory = categoryOptions.find((option) => option.value === formdata.category);
        filterText.push(`Category: ${selectedCategory?.label || formdata.category}`);
      }
      if (formdata.showOnlyFinalBalance) filterText.push('View: Final Balance Summary');
      
      doc.setFont(undefined, 'normal');
      doc.text(filterText.join(' | '), 45, currentY);
      currentY += 12;
    }
    
    // Table columns
    const tableColumn = formdata.showOnlyFinalBalance 
      ? ["Txn ID", "Date", "Account Type", "Customer Name", "Description", "Debit (THB)", "Received Money (THB)", "Final Balance (THB)"]
      : ["Txn ID", "Date", "Customer Name", "Account Type", "Description", "Debit (THB)", "Received Money (THB)", "Balance (THB)"];
    const tableRows = [];

    let runningBalance = 0;
    visibleRecords.forEach((row) => {
      const debit = Number(row.debit_amount) || 0;
      const credit = Number(row.credit_amount) || 0;
      
      // For final balance mode, calculate balance for each customer WITHOUT accumulation
      if (formdata.showOnlyFinalBalance) {
        // Each row represents a complete customer summary, so balance is just debit - credit
        const customerBalance = debit - credit;
        
        tableRows.push([
          row.transaction_id || '-',
          formatDisplayDate(row.date),
          row.account_type || '-',
          row.customer_name || 'Unknown Customer',
          row.description || '-',
          debit.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          credit.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          customerBalance.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        ]);
      } else {
        // For detailed ledger, calculate running balance
        runningBalance += debit - credit;
        
        tableRows.push([
          row.transaction_id || '-',
          formatDisplayDate(row.date),
          row.customer_name || 'Unknown Customer',
          row.account_type || '-',
          row.description || '-',
          debit.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          credit.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          runningBalance.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        ]);
      }
    });

    // Add totals row - adjust colSpan based on showOnlyFinalBalance
    const totalColSpan = formdata.showOnlyFinalBalance ? 5 : 5;
    tableRows.push([
      { content: "TOTALS:", colSpan: totalColSpan, styles: { halign: 'right', fontStyle: 'bold' } },
      visibleTotals.debit.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      visibleTotals.credit.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      visibleTotals.balance.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: currentY,
      margin: { left: 5, right: 5 }, // Reduced left margin to move table left
      theme: "grid",
      headStyles: { 
        fillColor: [200, 220, 240], // Soft light blue
        textColor: [40, 60, 100], // Soft dark blue
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        lineWidth: 0.1,
        lineColor: [180, 200, 230] // Soft blue border
      },
      styles: { 
        fontSize: 8,
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [200, 200, 200],
        valign: 'middle'
      },
      columnStyles: {
        0: { cellWidth: 25, halign: 'center' },
        1: { cellWidth: 25, halign: 'center' },
        2: { cellWidth: 40 },
        3: { cellWidth: 35 },
        4: { cellWidth: 'auto' },
        5: { cellWidth: 28, halign: 'right' },
        6: { cellWidth: 28, halign: 'right' },
        7: { cellWidth: 32, halign: 'right' }
      },
      didParseCell: function(data) {
        // Style the totals row
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fillColor = [240, 248, 255];
          data.cell.styles.textColor = [0, 0, 0];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 9;
          data.cell.styles.lineWidth = 0.5;
          data.cell.styles.lineColor = [41, 128, 185];
        }
      },
      didDrawPage: function(data) {
        // Footer with page numbers
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(128);
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.text(
            `Page ${i} of ${pageCount}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
        }
      }
    });

    const summaryY = (doc.lastAutoTable?.finalY || currentY) + 10;
    const summaryBoxX = pageWidth - 125;
    const summaryBoxWidth = 115;
    const summaryRowHeight = 8;
    const summaryLabelX = summaryBoxX + 48;
    const amountX = summaryBoxX + summaryBoxWidth - 4;

    doc.setFillColor(248, 249, 251);
    doc.setDrawColor(220, 224, 230);
    doc.rect(summaryBoxX, summaryY, summaryBoxWidth, summaryRowHeight * 3 + 8, 'FD');

    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(35, 35, 35);
    doc.text('TOTALS:', summaryBoxX + 4, summaryY + 6);

    doc.setFont(undefined, 'normal');
    doc.setTextColor(198, 40, 40);
    doc.text('Debit:', summaryLabelX, summaryY + 6);
    doc.text(visibleTotals.debit.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), amountX, summaryY + 6, { align: 'right' });

    doc.setTextColor(46, 125, 50);
    doc.text('Received:', summaryLabelX, summaryY + 14);
    doc.text(visibleTotals.credit.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), amountX, summaryY + 14, { align: 'right' });

    doc.setFont(undefined, 'bold');
    doc.setTextColor(21, 101, 192);
    doc.text('Balance:', summaryLabelX, summaryY + 22);
    doc.text(visibleTotals.balance.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), amountX, summaryY + 22, { align: 'right' });

    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(90, 90, 90);
    doc.text(
      `Balance = Total Debit - Received Money`,
      summaryBoxX + 4,
      summaryY + 30,
      { maxWidth: summaryBoxWidth - 8 }
    );

    doc.save(`customer_ledger_${fmt(new Date(), "ddMMyyyy_HHmmss")}.pdf`);
    toast.success("PDF exported successfully!");
  };

  const exportToExcel = () => {
    try {
      const visibleRecords = getVisibleRecordsForExport();
      const visibleTotals = getVisibleTotalsForExport(visibleRecords);
      const activeCustomerName = getActiveCustomerName();

      if (!visibleRecords.length) {
        toast.warning("No visible records to export");
        return;
      }

      // Prepare data for Excel export
      const excelData = [];
      
      // Add header information
      excelData.push(['CUSTOMER LEDGER REPORT']);
      excelData.push([]);
      excelData.push([`Generated: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`]);
      
      // Add filter information if applicable
      if (activeCustomerName || formdata.from || formdata.to || formdata.accounttype || formdata.showOnlyFinalBalance || formdata.recordStatus !== 'all' || formdata.category) {
        excelData.push([]);
        excelData.push(['Report Filters:']);
        if (activeCustomerName) excelData.push([`Customer: ${activeCustomerName}`]);
        if (formdata.accounttype) excelData.push([`Account Type: ${formdata.accounttype}`]);
        if (formdata.from && formdata.to) {
          excelData.push([`Period: ${formatDisplayDate(formdata.from)} to ${formatDisplayDate(formdata.to)}`]);
        }
        if (formdata.recordStatus !== 'all') excelData.push([`Record Status: ${formdata.recordStatus}`]);
        if (formdata.category) {
          const selectedCategory = categoryOptions.find((option) => option.value === formdata.category);
          excelData.push([`Category: ${selectedCategory?.label || formdata.category}`]);
        }
        if (formdata.showOnlyFinalBalance) excelData.push(['View: Final Balance Summary']);
      }
      
      excelData.push([]);
      
      // Add table headers
      const headers = formdata.showOnlyFinalBalance 
        ? ['Txn ID', 'Date', 'Account Type', 'Customer Name', 'Description', 'Debit (THB)', 'Credit (THB)', 'Final Balance (THB)']
        : ['Txn ID', 'Date', 'Customer Name', 'Account Type', 'Description', 'Debit (THB)', 'Credit (THB)', 'Balance (THB)'];
      excelData.push(headers);
      
      // Add data rows
      let runningBalance = 0;
      visibleRecords.forEach((row) => {
        const debit = Number(row.debit_amount) || 0;
        const credit = Number(row.credit_amount) || 0;
        
        if (formdata.showOnlyFinalBalance) {
          const customerBalance = debit - credit;
          excelData.push([
            row.transaction_id || '-',
            formatDisplayDate(row.date),
            row.account_type || '-',
            row.customer_name || 'Unknown Customer',
            row.description || '-',
            debit.toFixed(2),
            credit.toFixed(2),
            customerBalance.toFixed(2)
          ]);
        } else {
          runningBalance += debit - credit;
          excelData.push([
            row.transaction_id || '-',
            formatDisplayDate(row.date),
            row.customer_name || 'Unknown Customer',
            row.account_type || '-',
            row.description || '-',
            debit.toFixed(2),
            credit.toFixed(2),
            runningBalance.toFixed(2)
          ]);
        }
      });
      
      // Add totals row
      const totalRow = formdata.showOnlyFinalBalance 
        ? ['', '', '', '', 'TOTAL', visibleTotals.debit.toFixed(2), visibleTotals.credit.toFixed(2), visibleTotals.balance.toFixed(2)]
        : ['', '', '', '', 'TOTAL', visibleTotals.debit.toFixed(2), visibleTotals.credit.toFixed(2), visibleTotals.balance.toFixed(2)];
      excelData.push(totalRow);
      
      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Customer Ledger');
      
      // Set column widths
      const colWidths = formdata.showOnlyFinalBalance 
        ? [{ wch: 15 }, { wch: 12 }, { wch: 18 }, { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 18 }]
        : [{ wch: 15 }, { wch: 12 }, { wch: 22 }, { wch: 18 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
      ws['!cols'] = colWidths;
      
      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `customer_ledger_${fmt(new Date(), "ddMMyyyy_HHmmss")}.xlsx`);
      
      toast.success("Excel exported successfully!");
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error("Failed to export Excel file");
    }
  };

  const exportCSVData = [
    { 
      transaction_id: 'Customer Ledger Report',
      date: '',
      customer_name: '',
      account_type: '',
      account_id: '',
      description: '',
      debit_amount: '',
      credit_amount: '',
      running_balance: ''
    },
    ...(getActiveCustomerName() ? [
      {
        transaction_id: `Customer: ${getActiveCustomerName()}`,
        date: '',
        customer_name: '',
        account_type: '',
        account_id: '',
        description: '',
        debit_amount: '',
        credit_amount: '',
        running_balance: ''
      }
    ] : []),
    { 
      transaction_id: `Generated: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`,
      date: '',
      customer_name: '',
      account_type: '',
      account_id: '',
      description: '',
      debit_amount: '',
      credit_amount: '',
      running_balance: ''
    },
    { 
      transaction_id: '',
      date: '',
      customer_name: '',
      account_type: '',
      account_id: '',
      description: '',
      debit_amount: '',
      credit_amount: '',
      running_balance: ''
    },
    // Add data rows
    ...getVisibleRecordsForExport().map((row, index, visibleRecords) => {
      let runningBalance = 0;
      visibleRecords.slice(0, index + 1).forEach(r => {
        runningBalance += (Number(r.debit_amount) || 0) - (Number(r.credit_amount) || 0);
      });
      
      return {
        transaction_id: row.transaction_id,
        date: formatDisplayDate(row.date),
        customer_name: row.customer_name,
        account_type: row.account_type,
        account_id: row.account_id,
        description: row.description,
        debit_amount: row.debit_amount,
        credit_amount: row.credit_amount,
        running_balance: runningBalance.toFixed(2)
      };
    })
  ];

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Fetch data filtered by receipt_type (category)
        const filterCondition = formdata.category ? { receipt_type: formdata.category } : {};
        const fetched = await fetchData("customer_ledger_entries", null, "id", filterCondition);
        
        // Fetch customers data
        const customers = await fetchComboData("customers", "name");
        setCustomerdata(customers);
        
        // Fetch company profile data for logo
        try {
          const companyData = await fetchData("company_profile", null, "id", {});
          if (companyData && companyData.length > 0) {
            setCompanyProfile(companyData[0]);
          }
        } catch (error) {
          console.error('Error fetching company profile:', error);
        }
        
        // Populate customer_name in ledger entries by matching account_id
        const dataWithCustomerNames = fetched.map(entry => {
          const customer = customers.find(c => c.id === parseInt(entry.account_id));
          return {
            ...entry,
            customer_name: customer?.name || 'Unknown Customer'
          };
        });
        
        setAllData(dataWithCustomerNames);
        setData(dataWithCustomerNames);
      } catch (e) {
        console.error("Error fetching data:", e);
        toast.error("Failed to load data");
      }
      setLoading(false);
    };
    loadData();
  }, [formdata.category]);

  useEffect(() => {
    const filterData = () => {
      const { from, to, accounttype, account_id, showOnlyFinalBalance, recordStatus } = formdata;
      let filtered = [...Alldata];

      console.log('Total records before filter:', filtered.length);
      console.log('Filter params:', { from, to, accounttype, account_id, recordStatus });

      // Apply search term filter
      if (searchTerm) {
        filtered = filtered.filter(r => 
          r.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.account_id?.toString().includes(searchTerm)
        );
        console.log('After search filter:', filtered.length);
      }

      // Apply customer name filter
      if (selectedCustomerFilter) {
        console.log('Available customer names in data:', [...new Set(filtered.map(r => r.customer_name))]);
        console.log('Looking for customer:', selectedCustomerFilter);
        filtered = filtered.filter(r => {
          const match = r.customer_name?.trim().toLowerCase() === selectedCustomerFilter.trim().toLowerCase();
          if (match) console.log('Match found:', r.customer_name);
          return match;
        });
        console.log('After customer name filter:', filtered.length, 'Selected customer:', selectedCustomerFilter);
      }

      // Date range filter
      const safeFrom = formatDateSafe(from);
      const safeTo = formatDateSafe(to);
      if (safeFrom && safeTo) {
        filtered = filtered.filter((r) => {
          const d = formatDateSafe(r.date);
          return d && d >= safeFrom && d <= safeTo;
        });
        console.log('After date filter:', filtered.length);
      }

      // Account type filter - ONLY filter if accounttype is explicitly set
      if (accounttype && accounttype !== '') {
        filtered = filtered.filter((r) => r.account_type === accounttype);
        console.log('After account type filter:', filtered.length, 'Type:', accounttype);
      }

      // Customer filter - show ALL account types for this customer
      if (account_id) {
        filtered = filtered.filter((r) => r.account_id === parseInt(account_id));
        console.log('After customer filter:', filtered.length, 'Customer ID:', account_id);
        console.log('Account types in filtered data:', [...new Set(filtered.map(r => r.account_type))]);
      }

      // Record Status filter (pending/completed)
      if (recordStatus && recordStatus !== 'all') {
        filtered = filtered.filter((r) => r.status === recordStatus);
        console.log('After record status filter:', filtered.length, 'Status:', recordStatus);
      }

      // Quick filter for status
      if (quickFilter !== 'all') {
        const today = new Date();
        filtered = filtered.filter(r => {
          const balance = (Number(r.debit_amount) || 0) - (Number(r.credit_amount) || 0);
          switch (quickFilter) {
            case 'pending':
              return balance > 0;
            case 'completed':
              return balance <= 0;
            case 'overdue':
              const recordDate = parseISO(r.date);
              const daysDiff = (today - recordDate) / (1000 * 60 * 60 * 24);
              return balance > 0 && daysDiff > 30;
            default:
              return true;
          }
        });
        console.log('After quick filter:', filtered.length);
      }

      // Apply final balance filter if enabled
      if (showOnlyFinalBalance) {
        // Group by customer (account_id) and calculate final balance for each
        const customerBalances = {};
        filtered.forEach(r => {
          const customerId = r.account_id;
          if (!customerBalances[customerId]) {
            // Find customer name from customerdata array
            const customerInfo = customerdata.find(c => c.id === parseInt(customerId));
            customerBalances[customerId] = {
              ...r,
              customer_name: customerInfo?.name || r.customer_name || 'Unknown Customer',
              debit_amount: 0,
              credit_amount: 0,
              final_balance: 0
            };
          }
          customerBalances[customerId].debit_amount += Number(r.debit_amount) || 0;
          customerBalances[customerId].credit_amount += Number(r.credit_amount) || 0;
        });
        
        // Calculate final balance and create summary records
        filtered = Object.values(customerBalances).map(customer => ({
          ...customer,
          final_balance: customer.debit_amount - customer.credit_amount,
          description: `Final Balance for ${customer.customer_name}`,
          transaction_id: 'BALANCE_SUMMARY'
        }));
        console.log('After final balance grouping:', filtered.length);
      }

      // Calculate totals based on filter status
      // If filtering by 'completed', show completed transaction totals
      // Otherwise, show only pending/non-completed records to prevent totals from growing infinitely
      let recordsToSum;
      let summaryLabel;
      
      if (formdata.recordStatus === 'completed') {
        // When filtering completed, sum all completed transactions
        recordsToSum = filtered.filter(r => r.status === 'completed');
        summaryLabel = 'Completed';
        console.log('Calculating totals for completed records');
      } else {
        // For 'all' or 'pending' filters, only sum pending records
        recordsToSum = filtered.filter(r => {
          // Exclude records with status = 'completed'
          if (r.status === 'completed') {
            return false;
          }
          // Also exclude records where balance is zero or negative (paid off)
          const balance = (Number(r.debit_amount) || 0) - (Number(r.credit_amount) || 0);
          return balance > 0;
        });
        summaryLabel = 'Pending';
        console.log('Calculating totals for pending records only');
      }

      const credit = recordsToSum.reduce((sum, r) => sum + (Number(r.credit_amount) || 0), 0);
      const debit = recordsToSum.reduce((sum, r) => sum + (Number(r.debit_amount) || 0), 0);
      console.log(`${summaryLabel} Records Count:`, recordsToSum.length);
      console.log(`Totals (${summaryLabel}) - Debit:`, debit, 'Credit:', credit, 'Balance:', debit - credit);
      
      setSummaryLabel(summaryLabel); // Store label for summary cards
      
      // Show warning if only Sales records found (backend data issue)
      if (account_id && filtered.length > 0) {
        const accountTypes = [...new Set(filtered.map(r => r.account_type))];
        if (accountTypes.length === 1 && accountTypes[0] === 'Sales' && debit === 0) {
          console.warn('⚠️ WARNING: Only Sales entries found with no Debit amounts.');
          console.warn('This indicates a backend data issue. Sales should be recorded as DEBIT (customer owes) not CREDIT.');
          console.warn('Please check that ledger_entries are being created correctly for:');
          console.warn('- Sales/Invoices → DEBIT (customer owes)');
          console.warn('- Payments received → CREDIT (customer paid)');
          console.warn('- Discounts given → CREDIT (reducing amount owed)');
        }
      }
      
      setTotals({ credit, debit, balance: debit - credit });

      setData(filtered);
      setCurrentPage(1); // Reset to first page when filters change
    };

    filterData();
  }, [formdata, Alldata, quickFilter, searchTerm, selectedCustomerFilter]);

  const getStatusBadge = (record) => {
    // Check if status is set in the database
    if (record.status === 'completed') {
      return <Tag color="success" icon={<CheckCircleOutlined />}>Completed</Tag>;
    }
    
    // Otherwise calculate based on balance
    const balance = (Number(record.debit_amount) || 0) - (Number(record.credit_amount) || 0);
    const recordDate = parseISO(record.date);
    const today = new Date();
    const daysDiff = (today - recordDate) / (1000 * 60 * 60 * 24);
    
    if (balance <= 0) {
      return <Tag color="success" icon={<CheckCircleOutlined />}>Completed</Tag>;
    } else if (daysDiff > 30) {
      return <Tag color="error" icon={<ExclamationCircleOutlined />}>Overdue</Tag>;
    } else {
      return <Tag color="warning" icon={<ClockCircleOutlined />}>Pending</Tag>;
    }
  };

  const handleRecordsPerPageChange = (value) => {
    setRecordsPerPage(Number(value));
    setCurrentPage(1);
  };

  // Pagination calculations
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = data.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(data.length / recordsPerPage);
  const tableTotals = data.reduce(
    (acc, record) => {
      const debit = Number(record.debit_amount) || 0;
      const credit = Number(record.credit_amount) || 0;
      acc.debit += debit;
      acc.credit += credit;
      acc.balance += debit - credit;
      return acc;
    },
    { debit: 0, credit: 0, balance: 0 }
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        pageNumbers.push(currentPage - 1);
        pageNumbers.push(currentPage);
        pageNumbers.push(currentPage + 1);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    return pageNumbers;
  };

  // Table columns configuration
  const columns = [
    {
      title: (
        <Checkbox
          onChange={handleSelectAll}
          checked={selectedRecords.length === currentRecords.length && currentRecords.length > 0}
        />
      ),
      key: 'select',
      width: 50,
      fixed: 'left',
      render: (_, record) => (
        <Checkbox
          checked={selectedRecords.some(r => r.id === record.id)}
          onChange={() => handleCheckboxChange(record)}
        />
      ),
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 150,
      sorter: (a, b) => {
        const aId = a.transaction_id || '';
        const bId = b.transaction_id || '';
        return aId.localeCompare(bId);
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => formatDisplayDate(date),
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      sortDirections: ['ascend', 'descend'],
      defaultSortOrder: 'descend',
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 180,
      render: (name, record) => (
        <strong style={{ color: '#1890ff' }}>
          {name || 
           customerdata.find(c => c.id === record.account_id)?.name || 
           'Unknown Customer'}
        </strong>
      ),
      sorter: (a, b) => {
        const aName = a.customer_name || customerdata.find(c => c.id === a.account_id)?.name || '';
        const bName = b.customer_name || customerdata.find(c => c.id === b.account_id)?.name || '';
        return aName.localeCompare(bName);
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Account Type',
      dataIndex: 'account_type',
      key: 'account_type',
      width: 150,
      render: (type) => <Tag>{type}</Tag>,
      sorter: (a, b) => (a.account_type || '').localeCompare(b.account_type || ''),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Debit',
      dataIndex: 'debit_amount',
      key: 'debit_amount',
      width: 130,
      align: 'right',
      render: (amount) => `฿${Number(amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`,
      sorter: (a, b) => (Number(a.debit_amount) || 0) - (Number(b.debit_amount) || 0),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Credit',
      dataIndex: 'credit_amount',
      key: 'credit_amount',
      width: 130,
      align: 'right',
      render: (amount) => `฿${Number(amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`,
      sorter: (a, b) => (Number(a.credit_amount) || 0) - (Number(b.credit_amount) || 0),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      align: 'center',
      fixed: 'right',
      render: (_, record) => getStatusBadge(record),
      sorter: (a, b) => {
        const getStatusValue = (record) => {
          if (record.status === 'completed') return 0;
          const balance = (Number(record.debit_amount) || 0) - (Number(record.credit_amount) || 0);
          if (balance <= 0) return 0;
          const recordDate = parseISO(record.date);
          const today = new Date();
          const daysDiff = (today - recordDate) / (1000 * 60 * 60 * 24);
          if (daysDiff > 30) return 2; // Overdue
          return 1; // Pending
        };
        return getStatusValue(a) - getStatusValue(b);
      },
      sortDirections: ['ascend', 'descend'],
    },
  ];

  return (
    <Layout>
      <Header title="📊 Customer Ledger Management" />
      <ToastContainer position="top-right" autoClose={3000} />

      <div style={{ padding: '24px', background: '#f0f2f5' }}>
        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Statistic
                title={`${summaryLabel} Credit`}
                value={Totals.credit}
                precision={2}
                prefix="฿"
                valueStyle={{ color: '#2e7d32', fontWeight: 600 }}
                suffix={<DownloadOutlined style={{ fontSize: 16 }} />}
              />
              <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                {summaryLabel === 'Completed' ? 'Total completed transactions' : 'Excludes completed bills'}
              </div>
            </Card>
          </Col>
          
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Statistic
                title={`${summaryLabel} Debit`}
                value={Totals.debit}
                precision={2}
                prefix="฿"
                valueStyle={{ color: '#c62828', fontWeight: 600 }}
                suffix={<DownloadOutlined style={{ fontSize: 16 }} />}
              />
              <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                {summaryLabel === 'Completed' ? 'Total completed transactions' : 'Excludes completed bills'}
              </div>
            </Card>
          </Col>
          
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ background: Totals.balance >= 0 ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' : 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Statistic
                title={`${summaryLabel} Balance`}
                value={Totals.balance}
                precision={2}
                prefix="฿"
                valueStyle={{ color: Totals.balance >= 0 ? '#1565c0' : '#e65100', fontWeight: 600 }}
                suffix={<DownloadOutlined style={{ fontSize: 16 }} />}
              />
              <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                {summaryLabel === 'Completed' ? 'Completed balance' : 'Outstanding amount'}
              </div>
            </Card>
          </Col>
          
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Statistic
                title="Total Records"
                value={data.length}
                valueStyle={{ color: '#6a1b9a', fontWeight: 600 }}
                suffix={<EyeOutlined style={{ fontSize: 16 }} />}
              />
            </Card>
          </Col>
        </Row>

        {/* Selected Records Summary */}
        {selectedRecords.length > 0 && (
          <Card style={{ marginBottom: 16, background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)', border: '1px solid #4fc3f7', boxShadow: '0 4px 12px rgba(3, 169, 244, 0.15)' }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space size="large">
                  <strong>📋 {selectedRecords.length} Record(s) Selected</strong>
                  <span>
                    Debit: <strong>฿{selectedTotals.debit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong>
                  </span>
                  <span>
                    Credit: <strong>฿{selectedTotals.credit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong>
                  </span>
                  <span>
                    Balance: <strong style={{ color: selectedTotals.balance >= 0 ? '#2e7d32' : '#c62828' }}>
                      ฿{selectedTotals.balance.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                    </strong>
                  </span>
                </Space>
              </Col>
              <Col>
                <Space>
                  <Button 
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleMarkAsCompleted}
                    disabled={selectedRecords.some(r => r.status === 'completed')}
                  >
                    Mark as Completed
                  </Button>
                  <Button 
                    icon={<ReloadOutlined />}
                    onClick={handleRevertCompleted}
                    disabled={!selectedRecords.every(r => r.status === 'completed')}
                  >
                    Revert to Pending
                  </Button>
                  <Button 
                    onClick={() => {
                      setSelectedRecords([]);
                      setSelectedTotals({ debit: 0, credit: 0, balance: 0 });
                    }}
                  >
                    Clear Selection
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        {/* Header with Search and Actions */}
        <Card style={{ marginBottom: 16, background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Space>
                <Badge count={data.length} showZero style={{ backgroundColor: '#52c41a' }}>
                  <h3 style={{ margin: 0 }}>Customer Ledger</h3>
                </Badge>
              </Space>
            </Col>
            <Col xs={24} md={12}>
              <Row gutter={[8, 8]} justify="end">
                <Col flex="auto">
                  <Input
                    placeholder="Search by Transaction ID, Description, Customer..."
                    prefix={<SearchOutlined />}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col>
                  <Space>
                    <Button 
                      icon={<FilterOutlined />}
                      onClick={() => setShowFilters(!showFilters)}
                      type={showFilters ? 'primary' : 'default'}
                    >
                      Filters
                    </Button>
                    <Button 
                      icon={<FilePdfOutlined />}
                      onClick={exportToPDF}
                      danger
                    >
                      PDF
                    </Button>
                    <Button 
                      icon={<FileExcelOutlined />}
                      onClick={exportToExcel}
                      style={{ background: '#107c41', color: 'white', borderColor: '#107c41' }}
                    >
                      Excel
                    </Button>
                    <Button 
                      icon={<ReloadOutlined />}
                      onClick={resetFilters}
                    >
                      Reset
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <Card 
            title={
              <Space>
                <FilterOutlined />
                <span>Advanced Filters</span>
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8 }}>
                    <CalendarOutlined /> From Date
                  </label>
                  <Input
                    type="date"
                    value={formdata.from}
                    onChange={(e) => setFormData({ ...formdata, from: e.target.value })}
                  />
                </div>
              </Col>
              
              <Col xs={24} sm={12} md={8} lg={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8 }}>
                    <CalendarOutlined /> To Date
                  </label>
                  <Input
                    type="date"
                    value={formdata.to}
                    onChange={(e) => setFormData({ ...formdata, to: e.target.value })}
                  />
                </div>
              </Col>
              
              <Col xs={24} sm={12} md={8} lg={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8 }}>
                    <TagOutlined /> Category
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={formdata.category}
                    onChange={(value) => setFormData({ ...formdata, category: value })}
                  >
                    {categoryOptions.map((cat, idx) => (
                      <Option key={idx} value={cat.value}>{cat.label}</Option>
                    ))}
                  </Select>
                </div>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8 }}>
                    <UserOutlined /> Customer Name
                  </label>
                  <Select
                    showSearch
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Select customer"
                    value={selectedCustomerFilter || undefined}
                    onChange={(value) => setSelectedCustomerFilter(value || '')}
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {customerdata.map((customer, idx) => (
                      <Option key={idx} value={customer.name}>{customer.name}</Option>
                    ))}
                  </Select>
                </div>
              </Col>
              
              <Col xs={24} sm={12} md={8} lg={6}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8 }}>
                    <CheckCircleOutlined /> Record Status
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={formdata.recordStatus}
                    onChange={(value) => setFormData({ ...formdata, recordStatus: value })}
                  >
                    <Option value="all">All Status</Option>
                    <Option value="pending">⏳ Pending</Option>
                    <Option value="completed">✅ Completed</Option>
                  </Select>
                </div>
              </Col>
              
              <Col xs={24}>
                <Checkbox
                  checked={formdata.showOnlyFinalBalance}
                  onChange={(e) => setFormData({ ...formdata, showOnlyFinalBalance: e.target.checked })}
                >
                  <strong>💰 Show Only Final Balance</strong>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    Display only one record per customer with their total balance amount
                  </div>
                </Checkbox>
              </Col>
            </Row>
          </Card>
        )}

        {/* Data Table */}
        <Card 
          title={
            <Space>
              <span>Customer Ledger Entries</span>
              <Tag color="blue">{data.length} entries found</Tag>
            </Space>
          }
        >
          <Table
            columns={columns}
            dataSource={currentRecords}
            loading={loading}
            rowKey={(record) => record.id}
            pagination={false}
            scroll={{ x: 1400 }}
            size="small"
            bordered
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)', fontWeight: 600 }}>
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1} colSpan={5} align="right">
                    <strong>TOTALS:</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <strong style={{ color: '#c62828' }}>฿{tableTotals.debit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    <strong style={{ color: '#2e7d32' }}>฿{tableTotals.credit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="center">
                    <strong style={{ color: tableTotals.balance >= 0 ? '#1565c0' : '#e65100' }}>
                      Balance: ฿{tableTotals.balance.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                    </strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          
          {/* Pagination Controls */}
          {!loading && data.length > 0 && (
            <Row justify="space-between" align="middle" style={{ marginTop: 16 }}>
              <Col>
                <Space>
                  <span>Show</span>
                  <Select 
                    value={recordsPerPage}
                    onChange={handleRecordsPerPageChange}
                    style={{ width: 80 }}
                  >
                    <Option value={10}>10</Option>
                    <Option value={25}>25</Option>
                    <Option value={50}>50</Option>
                    <Option value={100}>100</Option>
                    <Option value={data.length}>All</Option>
                  </Select>
                  <span>
                    entries (Showing {indexOfFirstRecord + 1}-{Math.min(indexOfLastRecord, data.length)} of {data.length})
                  </span>
                </Space>
              </Col>

              {totalPages > 1 && (
                <Col>
                  <Space>
                    <Button 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    
                    {getPageNumbers().map((pageNumber, idx) => (
                      pageNumber === '...' ? (
                        <span key={`ellipsis-${idx}`}>...</span>
                      ) : (
                        <Button 
                          key={pageNumber}
                          type={currentPage === pageNumber ? 'primary' : 'default'}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </Button>
                      )
                    ))}
                    
                    <Button 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </Space>
                </Col>
              )}
            </Row>
          )}
        </Card>
      </div>
    </Layout>
  );
}

